/**
 * Where an API error's fields actually live, across the shapes this backend
 * emits.
 *
 * FastAPI's own convention is `{"detail": ...}`, and every hand-rolled `fetch`
 * caller here was written against it. But `apps/api/main.py`'s HTTPException
 * handler returns a dict `detail` AS the body, unwrapped — so a route raising
 * `HTTPException(400, detail={"code": ..., "message": ...})` puts those keys at
 * the TOP level and `body.detail` does not exist at all. Reading the nested
 * path against a flat body yields `undefined` silently, with no type error and
 * no failed request: the call looks like it worked and the branch simply never
 * fires. That is how five hooks ended up showing generic fallback copy in place
 * of the specific sentence the server had written for the visitor.
 *
 * The three shapes, and what tells them apart:
 *
 *   ResponseSchema.fail   `{"success": false, "error": {code, message, …}}`
 *                         → `error` is an OBJECT
 *   flat dict detail      `{"error": "slug", "code": …, "message": …}`
 *                         → `error` is a STRING
 *   FastAPI default       `{"detail": {code, …}}`
 *                         → `detail` is an object
 *
 * `{"detail": "text"}` and pydantic's `{"detail": [{loc, msg}, …]}` carry no
 * field block at all and return null, so callers keep whatever fallback they
 * already had. `apps/web/hooks/api-fetcher.ts` reads the same three shapes for
 * the SWR path; this is the reader for callers that hold their own `fetch`.
 */

function isFieldBlock(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** The object carrying an error's fields, or null when the body has none. */
export function apiErrorFields(body: unknown): Record<string, unknown> | null {
  if (!isFieldBlock(body)) return null

  const nested = body.error
  if (isFieldBlock(nested)) return nested

  const detail = body.detail
  if (isFieldBlock(detail)) {
    // `{"detail": {"success": false, "error": {…}}}` — a ResponseSchema body
    // that kept FastAPI's wrapper, which is what happens if the handler in
    // main.py is ever bypassed. Descend so the fields are still reachable.
    const detailError = detail.error
    return isFieldBlock(detailError) ? detailError : detail
  }

  // Flat: the handler unwrapped a dict detail onto the body itself. Require a
  // field we would actually read, so an arbitrary success payload that reached
  // here by mistake is reported as "no error block" rather than as an error.
  if (
    typeof body.code === 'string' ||
    typeof body.message === 'string' ||
    typeof nested === 'string'
  )
    return body

  return null
}

/** The machine-readable error code, whichever shape carried it. */
export function apiErrorCode(body: unknown): string | undefined {
  const code = apiErrorFields(body)?.code
  return typeof code === 'string' ? code : undefined
}

/** The server's human-facing sentence, whichever shape carried it. */
export function apiErrorMessage(body: unknown): string | undefined {
  const message = apiErrorFields(body)?.message
  return typeof message === 'string' ? message : undefined
}

/** A numeric field (`needed`, `balance`, …), whichever shape carried it. */
export function apiErrorNumber(body: unknown, key: string): number | undefined {
  const value = apiErrorFields(body)?.[key]
  return typeof value === 'number' ? value : undefined
}

/**
 * The human sentence a *string* `detail` carries — FastAPI's plain
 * `HTTPException(detail="…")` and pydantic's `{"detail": [{loc, msg}, …]}`.
 *
 * Those shapes have no field block, so `apiErrorFields` returns null for them
 * and the accessors above return undefined. A caller that shows server text to
 * a person needs BOTH: `apiErrorMessage` for the structured shapes and this for
 * the string one. Moved here from `hooks/api-fetcher.ts`, which still uses it,
 * so there is one reader rather than one per fetch caller.
 */
export function apiErrorDetailText(body: unknown): string | undefined {
  if (typeof body !== 'object' || body === null) return undefined
  const detail = (body as { detail?: unknown }).detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    if (first && typeof first === 'object' && 'msg' in first) {
      const msg = (first as { msg?: unknown }).msg
      if (typeof msg === 'string') return msg
    }
  }
  return undefined
}

/**
 * A string out of the error's own `details` object — the structured payload
 * `ResponseSchema.fail_with_status(details=…)` attaches, e.g. the `suggested`
 * slug a caller retries with after a brand-name conflict.
 */
export function apiErrorDetailString(
  body: unknown,
  key: string,
): string | undefined {
  const details = apiErrorFields(body)?.details
  if (!isFieldBlock(details)) return undefined
  const value = details[key]
  return typeof value === 'string' ? value : undefined
}
